import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  adminActionIcons,
  adminButton,
  adminField,
  adminFocusable,
  adminSidePanel,
  adminTokens as a,
} from '../admin-ui';
import {
  fetchScormExportContext,
  useAssignAccounts,
  useAssignedAccountIds,
  useClientAccounts,
  type AccountAssignmentResult,
  type AssignmentPackageOutcome,
  type AssignableContentType,
  type ServerAssignDeployResult,
} from '../api/accountAssignmentApi';
import {
  buildPublishDeployTargets,
  fetchExportPackageInfo,
  fetchLessonPublishState,
} from '../api/publishApi';
import {
  buildCourseDeployPackage,
  buildLessonDeployPackage,
  deployPackagesByAccount,
  type DeployAccountInput,
  type DeployAccountResult,
} from '../api/courseDeployApi';
import { loadCoursePointers } from '../api/exportSourceLoader';
import { healReferencedLessonPackages, prepareLessonHealTargets } from '../api/lessonPackageHeal';
import { useAuth } from '../auth';

type AssignmentFilter = 'all' | 'notAssigned' | 'assigned';

interface AccountPackageOutcome {
  accountSlug: string;
  outcome: AssignmentPackageOutcome;
}

interface PackageAccountSummary {
  accountSlug: string;
  status: 'pending' | 'copied' | 'failed' | 'deferred';
  reason?: string;
}

const ACCOUNT_ROW_HEIGHT = 32;
const ACCOUNT_LIST_OVERSCAN = 6;

export interface AccountAssignmentPanelProps {
  contentType: AssignableContentType;
  contentId: string;
  title: string;
  initialFilter: AssignmentFilter;
  /**
   * True when the content is Published in the Emtrain context. Newly assigned
   * accounts then receive their package immediately, so the content is
   * exportable there with no separate step (spec: assigning Published content
   * auto-deploys to the account). Draft content assigns without packaging.
   */
  isPublished?: boolean;
  /** Course timed-course flag — carried into the packaged runtime. */
  enforceSequence?: boolean;
  onClose: () => void;
  onAssigned?: (result: AccountAssignmentResult) => void;
}

export function AccountAssignmentPanel({
  contentType,
  contentId,
  title,
  initialFilter,
  isPublished = false,
  enforceSequence = false,
  onClose,
  onAssigned,
}: AccountAssignmentPanelProps) {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<AssignmentFilter>(initialFilter);
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(() => new Set());
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [isPreparingPackages, setIsPreparingPackages] = useState(false);
  const [packagesReadyNote, setPackagesReadyNote] = useState<string | null>(null);
  const [packagesDeferredNote, setPackagesDeferredNote] = useState<string | null>(null);
  const [packageFailures, setPackageFailures] = useState<DeployAccountResult[]>([]);
  /** Scopes that published fine but with degraded delivery (e.g. invalidation failed) — never silent. */
  const [packageWarnings, setPackageWarnings] = useState<Array<{ accountSlug: string; warning: string }>>([]);
  const [listScrollTop, setListScrollTop] = useState(0);
  const [listViewportHeight, setListViewportHeight] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  // The account ids being added by the in-flight assign — the deploy set once
  // the assignment write succeeds.
  const pendingDeployAccountIdsRef = useRef<string[]>([]);
  const {
    data: accounts = [],
    isLoading: isLoadingAccounts,
    isError: didAccountLoadFail,
  } = useClientAccounts();
  const {
    data: assignedAccountIds = [],
    isLoading: isLoadingAssigned,
    isError: didAssignedLoadFail,
  } = useAssignedAccountIds(contentType, contentId);

  // Assigning Published content makes it exportable in the new accounts with
  // no separate step. The SERVER already copied the published base packages
  // as part of the assignment write (NA-210 auto-deploy) — re-deploying those
  // scopes here was a straight duplicate: double uploads, double
  // invalidations, and two deploys racing over the same propagation metadata
  // ("a second deploy fixed it"). The client-side build now covers ONLY
  // accounts with customizations, which the server copy deliberately leaves
  // to an account-resolved build. Failures never roll the assignment back —
  // like publish, the failed accounts stay retryable via publish-update.
  const deployToNewlyAssignedAccounts = async (serverDeploys?: ServerAssignDeployResult[]) => {
    const newAccountIds = pendingDeployAccountIdsRef.current;
    pendingDeployAccountIdsRef.current = [];
    if (newAccountIds.length === 0 || (!isPublished && !serverDeploys?.length)) return;

    setIsPreparingPackages(true);
    setPackageFailures([]);
    setPackageWarnings([]);
    setPackagesReadyNote(null);
    setPackagesDeferredNote(null);
    // Keep assignment results visible even when the follow-up context fetch or
    // client-side package work fails.
    const failures: DeployAccountResult[] = (serverDeploys ?? [])
      .filter((result) => !result.success)
      .map((result) => ({ accountSlug: result.accountSlug, success: false, error: result.error }));
    const warnings: Array<{ accountSlug: string; warning: string }> = (serverDeploys ?? [])
      .filter((result) => result.warning)
      .map((result) => ({ accountSlug: result.accountSlug, warning: result.warning ?? '' }));
    const outcomes: AccountPackageOutcome[] = (serverDeploys ?? []).flatMap((result) =>
      (result.packageOutcomes ?? []).map((outcome) => ({ accountSlug: result.accountSlug, outcome }))
    );
    const accountSummaries: PackageAccountSummary[] = (serverDeploys ?? []).map((result) => ({
      accountSlug: result.accountSlug,
      status: result.requiresClientDeploy === true ? 'pending' : result.success ? 'copied' : 'failed',
      ...(result.error ? { reason: result.error } : {}),
    }));
    const recordPackageOutcome = (accountSlug: string, outcome: AssignmentPackageOutcome) => {
      const existingIndex = outcomes.findIndex((entry) =>
        entry.accountSlug === accountSlug
        && entry.outcome.contentPublicId === outcome.contentPublicId
        && entry.outcome.contentType === outcome.contentType
      );
      const entry = { accountSlug, outcome };
      if (existingIndex < 0) outcomes.push(entry);
      else outcomes[existingIndex] = entry;
    };
    const recordAccountSummary = (summary: PackageAccountSummary) => {
      const existingIndex = accountSummaries.findIndex((entry) => entry.accountSlug === summary.accountSlug);
      if (existingIndex < 0) accountSummaries.push(summary);
      else accountSummaries[existingIndex] = summary;
    };
    const recordTopLevelNotAttempted = (
      accountSlug: string,
      status: 'deferred' | 'failed',
      reason: string,
    ) => {
      recordAccountSummary({ accountSlug, status, reason });
      recordPackageOutcome(accountSlug, {
        contentPublicId: contentId,
        contentType,
        title,
        status: 'not_attempted',
        reason,
      });
    };
    // The server may know content was published after this panel was opened.
    // Always retain its results, but defer client builds until the view refreshes.
    if (!isPublished) {
      for (const summary of accountSummaries.filter((entry) => entry.status === 'pending')) {
        const reason = 'Reload to refresh the publication state, then publish an update to prepare this account-specific package.';
        recordTopLevelNotAttempted(summary.accountSlug, 'deferred', reason);
      }
      setPackageFailures(failures);
      setPackageWarnings(warnings);
      setIsPreparingPackages(false);
      return;
    }
    try {
      // Fetched after the assignment write on purpose: the scorm-context
      // account list is filtered to active assignments, so the accounts just
      // added are only present now.
      const context = await fetchScormExportContext(contentType, contentId);

      // The server's copy results are part of the outcome — surface them.
      const wanted = new Set(newAccountIds);
      const assignedTargets = buildPublishDeployTargets(context, { enforceSequence }).filter(
        (target) => !target.base && wanted.has(String(target.externalAccountId))
      );
      // No server results (Draft content never deploys; older API) → the
      // panel remains responsible for every newly assigned account. With
      // results, the server's EXPLICIT disposition decides which scopes need
      // a client-side build: it withholds the pointer only for course-level
      // customization, while the transitive hasCustomizations flag also fires
      // for lesson-only customization the server copy already handled —
      // re-deploying those was the residual double deploy. Older servers
      // without the disposition fall back to the flag.
      const hasDisposition = (serverDeploys ?? []).some((result) => result.requiresClientDeploy !== undefined);
      const clientTargets = serverDeploys !== undefined
        ? assignedTargets.filter((target) => (hasDisposition
            ? serverDeploys.some((result) =>
                result.accountSlug === target.accountSlug && result.requiresClientDeploy === true)
            : target.hasCustomizations === true))
        : assignedTargets;
      let deferredCount = 0;

      if (clientTargets.length > 0) {
        if (context.hasPendingEdits) {
          // Base context (or a contained base lesson) is mid-edit: deploying
          // now would build from the draft layer and ship unpublished
          // (un-validated) edits. Defer packaging — the assignment stands, and
          // the next publish update deploys the published content too.
          deferredCount = clientTargets.length;
          for (const target of clientTargets) {
            const reason = 'Account-resolved package deferred until the next publish update because content has pending edits.';
            recordTopLevelNotAttempted(target.accountSlug, 'deferred', reason);
          }
          setPackagesDeferredNote(
            clientTargets.length === 1
              ? 'One account needs an account-resolved package; it will be prepared at the next publish update.'
              : `${clientTargets.length} accounts need account-resolved packages; they will be prepared at the next publish update.`
          );
        } else {
          // A course deploy ships a POINTER whose referenced lesson packages
          // must exist for the scope first — the server withheld its pointer
          // copy exactly when a lesson copy failed, and uploading one here
          // anyway would create the dangling pointer it refused. Same heal +
          // fail-closed contract as the publish flow.
          let pointerTargets: DeployAccountInput[] = clientTargets;
          if (contentType === 'course') {
            try {
              // Each customized account deploys an ACCOUNT-RESOLVED pointer
              // whose ref set can differ from base (client-added/suppressed
              // lessons) — heal each target against ITS OWN refs.
              const blockedSlugs = new Set<string>();
              const healDeps = {
                fetchPackageInfo: (lessonId: string, accountSlug?: string) =>
                  fetchExportPackageInfo(lessonId, accountSlug, 'reference'),
                fetchLessonPublishState,
                deployLesson: async (lessonId: string, accounts: DeployAccountInput[], lessonPublishedAt?: string) => {
                  let lessonTargets;
                  try {
                    lessonTargets = await prepareLessonHealTargets(lessonId, accounts);
                  } catch (error) {
                    return accounts.map(account => ({
                      accountSlug: account.accountSlug,
                      success: false,
                      error: error instanceof Error ? error.message : 'Lesson preparation failed.',
                    }));
                  }
                  const lessonResponse = await deployPackagesByAccount({
                    packageId: lessonId,
                    accounts: lessonTargets,
                    buildPackage: (externalAccountId) =>
                      buildLessonDeployPackage({
                        lessonId,
                        exportedBy: user?.id || 'unknown',
                        accountId: externalAccountId,
                      }),
                    onPhase: () => {},
                    contentPublishedAt: lessonPublishedAt,
                  });
                  return lessonResponse.results;
                },
              };
              for (const target of clientTargets) {
                const pointers = await loadCoursePointers(contentId, target.externalAccountId ?? null);
                const refIds = pointers[0]?.lessonRefs.map((ref) => ref.id) ?? [];
                const heal = await healReferencedLessonPackages(refIds, [target], healDeps);
                failures.push(...heal.results.filter((result) => !result.success));
                warnings.push(...heal.results
                  .filter((result) => result.warning)
                  .map((result) => ({ accountSlug: result.accountSlug, warning: result.warning ?? '' })));
                for (const result of heal.results) {
                  recordPackageOutcome(result.scopeSlug, {
                    contentPublicId: result.contentPublicId,
                    contentType: result.contentType,
                    status: result.success ? 'copied' as const : 'failed' as const,
                    ...(result.error || result.warning
                      ? { reason: result.error ?? result.warning }
                      : {}),
                  });
                }
                for (const slug of heal.blockedSlugs) blockedSlugs.add(slug);
              }
              if (blockedSlugs.size > 0) {
                pointerTargets = clientTargets.filter((target) => !blockedSlugs.has(target.accountSlug));
                for (const target of clientTargets) {
                  if (!blockedSlugs.has(target.accountSlug)) continue;
                  const reason = 'Course package withheld because a referenced lesson package could not be prepared.';
                  recordTopLevelNotAttempted(target.accountSlug, 'failed', reason);
                  failures.push({
                    accountSlug: target.accountSlug,
                    success: false,
                    error: 'Course package withheld for this account: its referenced lesson packages could not be prepared. Resolve the lesson issue, then publish an update.',
                  });
                }
              }
            } catch (error) {
              // Fail closed: without a verified heal, no customized scope gets
              // the pointer.
              const reason = `Course package not prepared — lesson packages could not be verified: ${
                error instanceof Error ? error.message : 'unknown error'
              } Publish an update to retry.`;
              for (const target of clientTargets) {
                failures.push({ accountSlug: target.accountSlug, success: false, error: reason });
                recordTopLevelNotAttempted(target.accountSlug, 'failed', reason);
              }
              pointerTargets = [];
            }
          }
          if (pointerTargets.length > 0) {
            const buildPackage = (externalAccountId: number | null) =>
              contentType === 'course'
                ? buildCourseDeployPackage({
                    courseId: contentId,
                    exportedBy: user?.id || 'unknown',
                    accountId: externalAccountId,
                  })
                : buildLessonDeployPackage({
                    lessonId: contentId,
                    exportedBy: user?.id || 'unknown',
                    accountId: externalAccountId,
                  });
            const response = await deployPackagesByAccount({
              packageId: contentId,
              accounts: pointerTargets,
              buildPackage,
              onPhase: () => {},
            });
            failures.push(...response.results.filter((result) => !result.success));
            warnings.push(...response.results
              .filter((result) => result.warning)
              .map((result) => ({ accountSlug: result.accountSlug, warning: result.warning ?? '' })));
            for (const result of response.results) {
              const outcome: AssignmentPackageOutcome = {
                contentPublicId: contentId,
                contentType,
                title,
                status: result.success ? 'copied' as const : 'failed' as const,
                ...(result.error || result.warning
                  ? { reason: result.error ?? result.warning }
                  : {}),
              };
              recordPackageOutcome(result.accountSlug, outcome);
              recordAccountSummary({
                accountSlug: result.accountSlug,
                status: result.success ? 'copied' : 'failed',
                ...(result.error || result.warning ? { reason: result.error ?? result.warning } : {}),
              });
            }
          }
        }
      }

      setPackageFailures(failures);
      setPackageWarnings(warnings);
      const readyCount = newAccountIds.length - deferredCount;
      const hasBlockingOutcome = outcomes.some(({ outcome }) =>
        outcome.status === 'failed'
        || outcome.status === 'not_attempted'
        || outcome.status === 'repair_required'
      );
      if (failures.length === 0 && !hasBlockingOutcome && readyCount > 0) {
        setPackagesReadyNote(
          readyCount === 1
            ? 'Published content is ready to export in the newly assigned account.'
            : `Published content is ready to export in the ${readyCount} newly assigned accounts.`
        );
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to prepare the published content.';
      for (const summary of accountSummaries.filter((entry) => entry.status === 'pending')) {
        const reason = `Account-specific package preparation could not finish: ${message}`;
        recordTopLevelNotAttempted(summary.accountSlug, 'deferred', reason);
      }
      setPackageFailures([...failures, {
        accountSlug: '(all)',
        success: false,
        error: message,
      }]);
      setPackageWarnings(warnings);
    } finally {
      setIsPreparingPackages(false);
    }
  };

  const { mutate: assignAccounts, isPending: isAssigning } = useAssignAccounts({
    onSuccess: (result) => {
      onAssigned?.(result);
      setSelectedAccountIds(new Set());
      setAssignmentError(null);
      void deployToNewlyAssignedAccounts(result.deploys);
    },
    onError: (error) => {
      pendingDeployAccountIdsRef.current = [];
      setAssignmentError(error.message || 'Failed to assign accounts');
    },
  });

  const assignedAccountIdSet = useMemo(
    () => new Set(assignedAccountIds),
    [assignedAccountIds]
  );

  const filteredAccounts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return accounts.filter((account) => {
      const isAssigned = assignedAccountIdSet.has(account.id);
      if (activeFilter === 'notAssigned' && isAssigned) return false;
      if (activeFilter === 'assigned' && !isAssigned) return false;
      if (!normalizedQuery) return true;

      return [
        account.name,
        account.id,
        account.externalId ?? '',
        account.subdomain ?? '',
      ].some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [accounts, activeFilter, assignedAccountIdSet, searchQuery]);

  useEffect(() => {
    setSearchQuery('');
    setActiveFilter(initialFilter);
    setSelectedAccountIds(new Set());
    setAssignmentError(null);
    setPackagesReadyNote(null);
    setPackagesDeferredNote(null);
    setPackageFailures([]);
    setPackageWarnings([]);
    pendingDeployAccountIdsRef.current = [];
    setListScrollTop(0);
    listRef.current?.scrollTo({ top: 0 });
  }, [contentId, initialFilter]);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    const updateViewportHeight = () => {
      setListViewportHeight(listElement.clientHeight);
    };

    updateViewportHeight();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(listElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setListScrollTop(0);
    listRef.current?.scrollTo({ top: 0 });
  }, [activeFilter, searchQuery]);

  const toggleAccount = (accountId: string) => {
    setSelectedAccountIds((current) => {
      const next = new Set(current);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const isBusy = isAssigning || isPreparingPackages;

  const handleAssign = () => {
    if (selectedAccountIds.size === 0 || isBusy) return;
    setAssignmentError(null);
    setPackagesReadyNote(null);
    setPackagesDeferredNote(null);
    setPackageFailures([]);
    setPackageWarnings([]);
    pendingDeployAccountIdsRef.current = Array.from(selectedAccountIds);
    assignAccounts({
      contentType,
      contentId,
      accountIds: Array.from(new Set([...assignedAccountIds, ...selectedAccountIds])),
    });
  };

  const selectedCount = selectedAccountIds.size;
  const assignedCount = assignedAccountIds.length;
  const isLoading = isLoadingAccounts || isLoadingAssigned;
  const didLoadFail = didAccountLoadFail || didAssignedLoadFail;
  const virtualStartIndex = Math.max(
    0,
    Math.floor(listScrollTop / ACCOUNT_ROW_HEIGHT) - ACCOUNT_LIST_OVERSCAN
  );
  const virtualVisibleCount = Math.ceil(
    (listViewportHeight || ACCOUNT_ROW_HEIGHT * 10) / ACCOUNT_ROW_HEIGHT
  ) + ACCOUNT_LIST_OVERSCAN * 2;
  const virtualEndIndex = Math.min(
    filteredAccounts.length,
    virtualStartIndex + virtualVisibleCount
  );
  const visibleAccounts = filteredAccounts.slice(virtualStartIndex, virtualEndIndex);
  const virtualTopPadding = virtualStartIndex * ACCOUNT_ROW_HEIGHT;
  const virtualBottomPadding = Math.max(
    0,
    (filteredAccounts.length - virtualEndIndex) * ACCOUNT_ROW_HEIGHT
  );

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    setListScrollTop(event.currentTarget.scrollTop);
  };

  const styles = useMemo(() => ({
    overlay: {
      position: 'fixed' as const,
      inset: 0,
      zIndex: 1100,
      display: 'flex',
      justifyContent: 'flex-end',
      backgroundColor: a.surface.scrim,
    },
    panel: {
      ...adminSidePanel.panel,
      width: 'clamp(310px, 35vw, 420px)',
      maxWidth: 'calc(100vw - clamp(16px, 4vw, 48px))',
      height: 'min(100dvh, calc(100dvh - 24px))',
      maxHeight: 'calc(100dvh - clamp(24px, 8dvh, 64px))',
      margin: 'clamp(60px, 4dvh, 100px) clamp(3px, 2vw, 2px) clamp(10px, 4dvh, 32px) 0',
      borderRadius: `${a.radius.panel} 0 0 ${a.radius.panel}`,
      overflow: 'hidden',
    },
    header: {
      ...adminSidePanel.header,
      padding: `${a.spacing.lg} ${a.spacing.lg} ${a.spacing.md}`,
    },
    title: adminSidePanel.title,
    subtitle: { ...adminSidePanel.subtitle, whiteSpace: 'normal' as const },
    closeButton: adminSidePanel.closeButton,
    controls: {
      padding: `${a.spacing.md} ${a.spacing.lg}`,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: a.spacing.sm,
      borderBottom: `1px solid ${a.border.subtle}`,
    },
    searchField: {
      position: 'relative' as const,
      width: '100%',
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '11px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: a.text.secondary,
      fontSize: '14px',
      lineHeight: 1,
      pointerEvents: 'none' as const,
    },
    searchInput: {
      ...adminField.input,
      width: '100%',
      height: '36px',
      paddingLeft: '34px',
    },
    filterRow: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: `${a.spacing.xs} ${a.spacing.md}`,
    },
    segmentGroup: {
      display: 'inline-flex',
      alignItems: 'stretch',
      border: `1px solid ${a.border.control}`,
      borderRadius: a.radius.control,
      overflow: 'hidden',
      flex: '0 1 auto',
    },
    segment: {
      ...adminFocusable,
      outlineOffset: '-3px',
      minHeight: '30px',
      padding: `0 ${a.spacing.sm}`,
      border: 'none',
      borderRight: `1px solid ${a.border.control}`,
      backgroundColor: a.surface.panel,
      color: a.text.secondary,
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: a.typography.caption.size,
      fontWeight: 'normal',
      whiteSpace: 'nowrap' as const,
    },
    segmentActive: {
      backgroundColor: a.action.primary,
      color: a.text.onPrimary,
      fontWeight: 600,
    },
    summary: {
      margin: 0,
      color: a.text.secondary,
      fontSize: a.typography.caption.size,
      lineHeight: a.typography.caption.lineHeight,
      whiteSpace: 'nowrap' as const,
    },
    list: {
      flex: 1,
      overflowY: 'auto' as const,
      backgroundColor: a.surface.panel,
      minHeight: 0,
      overscrollBehavior: 'contain' as const,
    },
    virtualListInner: {
      position: 'relative' as const,
      minHeight: '100%',
    },
    virtualSpacer: {
      flex: '0 0 auto',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      height: `${ACCOUNT_ROW_HEIGHT}px`,
      padding: `0 ${a.spacing.lg}`,
    },
    accountName: {
      margin: 0,
      color: a.text.primary,
      fontSize: a.typography.label.size,
      fontWeight: 400,
      lineHeight: 1.25,
    },
    checkbox: {
      ...adminFocusable,
      width: '14px',
      height: '14px',
      flex: '0 0 auto',
      accentColor: a.action.primary,
      cursor: 'pointer',
    },
    checkIcon: {
      width: '14px',
      height: '14px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: a.status.success.text,
      fontSize: '13px',
      fontWeight: 700,
      flex: '0 0 auto',
    },
    stateText: adminSidePanel.stateText,
    errorText: {
      margin: 0,
      color: a.status.error.text,
      fontSize: a.typography.label.size,
      lineHeight: a.typography.label.lineHeight,
    },
    footer: {
      ...adminSidePanel.footer,
      justifyContent: 'flex-end',
      padding: `${a.spacing.md} ${a.spacing.lg}`,
    },
    footerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
    },
    cancelButton: adminButton.secondary,
    assignButton: adminButton.primary,
    assignButtonDisabled: adminButton.disabled,
  }), []);

  return (
    <div
      style={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isBusy) {
          onClose();
        }
      }}
    >
      <aside
        style={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={`Assign accounts for ${title}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header style={styles.header}>
          <div>
            <h2 style={styles.title}>Assign to accounts</h2>
            <p style={styles.subtitle}>{title}</p>
          </div>
          <button
            type="button"
            aria-label="Close account assignment"
            onClick={onClose}
            disabled={isBusy}
            style={styles.closeButton}
          >
            ×
          </button>
        </header>

        <div style={styles.controls}>
          <div style={styles.searchField}>
            <span style={styles.searchIcon} aria-hidden="true"><FontAwesomeIcon icon={adminActionIcons.search} /></span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search client accounts..."
              aria-label="Search client accounts"
              style={styles.searchInput}
            />
          </div>
          <div style={styles.filterRow}>
            <div style={styles.segmentGroup} role="tablist" aria-label="Account assignment filters">
              {([
                ['all', 'All'],
                ['notAssigned', 'Not assigned'],
                ['assigned', `Already assigned (${assignedCount})`],
              ] as const).map(([filter, label], index) => (
                <button
                  key={filter}
                  type="button"
                  role="tab"
                  aria-selected={activeFilter === filter}
                  onClick={() => setActiveFilter(filter)}
                  style={{
                    ...styles.segment,
                    ...(index === 2 ? { borderRight: 'none' } : {}),
                    ...(activeFilter === filter ? styles.segmentActive : {}),
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <p style={styles.summary}>
              {selectedCount} selected
            </p>
          </div>
          {assignmentError && <p style={styles.errorText}>{assignmentError}</p>}
          {isPreparingPackages && (
            <p style={styles.summary} role="status">
              Preparing published content for the newly assigned accounts…
            </p>
          )}
          {packagesReadyNote && <p style={styles.summary} role="status">{packagesReadyNote}</p>}
          {packagesDeferredNote && <p style={styles.summary} role="status">{packagesDeferredNote}</p>}
          {packageFailures.length > 0 && (
            <div role="alert">
              <p style={styles.errorText}>
                The published content couldn't be made available in these accounts — publish an
                update from the editor to retry:
              </p>
              {packageFailures.map((failure, index) => (
                <p key={`${failure.accountSlug}-${index}`} style={styles.errorText}>
                  {failure.accountSlug}: {failure.error ?? 'Failed'}
                </p>
              ))}
            </div>
          )}
          {packageWarnings.length > 0 && (
            <div role="status">
              <p style={styles.summary}>
                ⚠ Published with delivery caveats — the update reaches learners as caches expire:
              </p>
              {packageWarnings.map((entry, index) => (
                <p key={`${entry.accountSlug}-warn-${index}`} style={styles.summary}>
                  {entry.accountSlug}: {entry.warning}
                </p>
              ))}
            </div>
          )}
        </div>

        <div
          ref={listRef}
          style={styles.list}
          onScroll={handleListScroll}
        >
          {isLoading ? (
            <p style={styles.stateText}>Loading accounts...</p>
          ) : didLoadFail ? (
            <p style={{ ...styles.stateText, color: a.status.error.text }}>
              Unable to load account assignments.
            </p>
          ) : filteredAccounts.length === 0 ? (
            <p style={styles.stateText}>No accounts match this view.</p>
          ) : (
            <div style={styles.virtualListInner}>
              {virtualTopPadding > 0 && (
                <div
                  aria-hidden="true"
                  style={{ ...styles.virtualSpacer, height: `${virtualTopPadding}px` }}
                />
              )}
              {visibleAccounts.map((account) => {
                const isAssigned = assignedAccountIdSet.has(account.id);
                const isSelected = selectedAccountIds.has(account.id);

                return (
                  <label
                    key={account.id}
                    style={{
                      ...styles.row,
                      cursor: isAssigned ? 'default' : 'pointer',
                    }}
                  >
                    {isAssigned ? (
                      <span style={styles.checkIcon} aria-label="Already assigned">✓</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleAccount(account.id)}
                        style={styles.checkbox}
                        aria-label={`Select ${account.name}`}
                      />
                    )}
                    <span>
                      <p style={styles.accountName}>{account.name}</p>
                    </span>
                  </label>
                );
              })}
              {virtualBottomPadding > 0 && (
                <div
                  aria-hidden="true"
                  style={{ ...styles.virtualSpacer, height: `${virtualBottomPadding}px` }}
                />
              )}
            </div>
          )}
        </div>

        <footer style={styles.footer}>
          <div style={styles.footerActions}>
            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              style={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAssign}
              disabled={selectedCount === 0 || isBusy}
              style={{
                ...styles.assignButton,
                ...(selectedCount === 0 || isBusy ? styles.assignButtonDisabled : {}),
              }}
            >
              {isAssigning ? 'Assigning...' : isPreparingPackages ? 'Preparing content…' : 'Assign'}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}
